import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { QuoteCacheDto } from "../models/quote-cache.model";
import { SessionCacheApiService } from "./session-cache-api.service";
import { LoginDetailsStorageService } from "./login-details-storage.service";

/** App sessionStorage keys bridged for quote cache (not stored in browser). */
export const QUOTE_DETAILS_STORAGE_KEYS = new Set([
  "quoteId",
  "quoteNo",
  "quoteNumber",
  "quoteStatus",
  "quoteAddressId",
  "addressTypeId",
  "quoteReferralLock",
  "quoteDate",
  "quoteVersionBound",
  "inceptionStatus",
  "InceptionStatusCode",
]);

const CACHE_TYPE = "quote";

@Injectable({
  providedIn: "root",
})
export class QuoteDetailsStorageService {
  private readonly sessionCacheApi = inject(SessionCacheApiService);
  private readonly loginDetailsStorage = inject(LoginDetailsStorageService);
  private readonly values = new Map<string, string>();
  private static bridgeInstalled = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private persistEnabled = true;
  private loaded = false;

  purgeNativeQuoteKeysFromBrowser(): void {
    if (typeof window === "undefined") {
      return;
    }
    const nativeRemove = Storage.prototype.removeItem;
    QUOTE_DETAILS_STORAGE_KEYS.forEach((key) => {
      try {
        nativeRemove.call(window.sessionStorage, key);
      } catch {
        /* ignore */
      }
    });
  }

  /** Chain after IndicationDetailsStorageService.initSessionStorageBridge(). */
  initSessionStorageBridge(): void {
    if (QuoteDetailsStorageService.bridgeInstalled || typeof window === "undefined") {
      return;
    }

    const storageProto = Object.getPrototypeOf(window.sessionStorage) as Storage;
    const wrappedGetItem = storageProto.getItem;
    const wrappedSetItem = storageProto.setItem;
    const wrappedRemoveItem = storageProto.removeItem;
    const store = this;

    storageProto.getItem = function (key: string): string | null {
      if (this === window.sessionStorage && QUOTE_DETAILS_STORAGE_KEYS.has(key)) {
        return store.getItem(key);
      }
      return wrappedGetItem.call(this, key);
    };

    storageProto.setItem = function (key: string, value: string): void {
      if (this === window.sessionStorage && QUOTE_DETAILS_STORAGE_KEYS.has(key)) {
        store.setItem(key, value);
        return;
      }
      wrappedSetItem.call(this, key, value);
    };

    storageProto.removeItem = function (key: string): void {
      if (this === window.sessionStorage && QUOTE_DETAILS_STORAGE_KEYS.has(key)) {
        store.removeItem(key);
        return;
      }
      wrappedRemoveItem.call(this, key);
    };

    QuoteDetailsStorageService.bridgeInstalled = true;
  }

  getItem(key: string): string | null {
    if (!QUOTE_DETAILS_STORAGE_KEYS.has(key)) {
      return null;
    }
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (!QUOTE_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    const str = String(value);
    this.values.set(key, str);
    if (key === "quoteNo") {
      this.values.set("quoteNumber", str);
    } else if (key === "quoteNumber") {
      this.values.set("quoteNo", str);
    }
    this.schedulePersist();
  }

  removeItem(key: string): void {
    if (!QUOTE_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.delete(key);
    if (key === "quoteNo") {
      this.values.delete("quoteNumber");
    } else if (key === "quoteNumber") {
      this.values.delete("quoteNo");
    }
    this.schedulePersist();
  }

  clear(): void {
    this.values.clear();
    this.loaded = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  disablePersist(): void {
    this.persistEnabled = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  enablePersist(): void {
    this.persistEnabled = true;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getCacheSessionId(): string | null {
    return this.loginDetailsStorage.getCacheSessionId();
  }

  buildCachePayload(overrides: Partial<QuoteCacheDto> = {}): QuoteCacheDto {
    const irCode = this.toCacheText(this.getItem("inceptionStatus"));
    const payload: QuoteCacheDto = {
      quoteId: this.toCacheInt(this.getItem("quoteId")),
      quoteNo: this.getQuoteNo(),
      quoteNameInsured: this.readSessionField("nameInsured") ?? "",
      quoteStatus: this.getItem("quoteStatus") ?? "",
      quoteVersionNo: this.toCacheInt(this.readSessionField("versionNo")),
      quoteAddressId: this.toCacheAddressId(this.getItem("quoteAddressId")),
      addressTypeId: this.toCacheAddressId(this.getItem("addressTypeId")),
      quoteReferralLock: this.toCacheInt(this.getItem("quoteReferralLock")),
      quoteDate: this.toCacheDateOrNull(this.getItem("quoteDate")),
      quoteVersionBound: this.toCacheInt(this.getItem("quoteVersionBound")),
      quoteBuildingId: this.readSessionField("buildingid") ?? "",
      irGencodeId: this.toCacheInt(this.getItem("InceptionStatusCode")),
      irCode,
      irDescription: this.resolveInceptionDescription(irCode),
      ...overrides,
    };

    return {
      quoteId: this.toCacheInt(payload.quoteId),
      quoteNo: payload.quoteNo ?? "",
      quoteNameInsured: payload.quoteNameInsured ?? "",
      quoteStatus: payload.quoteStatus ?? "",
      quoteVersionNo: this.toCacheInt(payload.quoteVersionNo),
      quoteAddressId: this.toCacheInt(payload.quoteAddressId),
      addressTypeId: this.toCacheInt(payload.addressTypeId),
      quoteReferralLock: this.toCacheInt(payload.quoteReferralLock),
      quoteDate: this.toCacheDateOrNull(
        payload.quoteDate != null ? String(payload.quoteDate) : null
      ),
      quoteVersionBound: this.toCacheInt(payload.quoteVersionBound),
      quoteBuildingId: payload.quoteBuildingId ?? "",
      irGencodeId: this.toCacheInt(payload.irGencodeId),
      irDescription: payload.irDescription ?? "",
      irCode: this.toCacheText(payload.irCode),
    };
  }

  applyFromCacheDto(data: QuoteCacheDto): void {
    if (data.quoteId !== undefined && data.quoteId !== null && data.quoteId !== 0) {
      this.values.set("quoteId", String(data.quoteId));
    }
    if (data.quoteNo) {
      const no = String(data.quoteNo);
      this.values.set("quoteNo", no);
      this.values.set("quoteNumber", no);
    }
    if (data.quoteStatus) {
      this.values.set("quoteStatus", String(data.quoteStatus));
    }
    if (data.quoteAddressId !== undefined && data.quoteAddressId !== null) {
      this.values.set(
        "quoteAddressId",
        this.formatAddressIdForSession("quoteAddressId", data.quoteAddressId)
      );
    }
    if (data.addressTypeId !== undefined && data.addressTypeId !== null) {
      this.values.set(
        "addressTypeId",
        this.formatAddressIdForSession("addressTypeId", data.addressTypeId)
      );
    }
    if (data.quoteReferralLock !== undefined) {
      this.values.set("quoteReferralLock", String(data.quoteReferralLock));
    }
    if (data.quoteDate !== undefined && data.quoteDate !== null && String(data.quoteDate).trim() !== "") {
      this.values.set("quoteDate", String(data.quoteDate));
    } else if (data.quoteDate === null) {
      this.values.delete("quoteDate");
    }
    if (data.quoteVersionBound !== undefined) {
      this.values.set("quoteVersionBound", String(data.quoteVersionBound));
    }
    if (data.quoteBuildingId) {
      this.writeSessionField("buildingid", String(data.quoteBuildingId));
    }
    if (this.toCacheText(data.irCode)) {
      this.values.set("inceptionStatus", String(data.irCode));
    }
    if (data.irGencodeId !== undefined && data.irGencodeId !== null && data.irGencodeId !== 0) {
      this.values.set("InceptionStatusCode", String(data.irGencodeId));
    }

    if (data.quoteNameInsured) {
      this.writeSessionField("nameInsured", String(data.quoteNameInsured));
    }
    if (data.quoteVersionNo !== undefined && data.quoteVersionNo !== null) {
      this.writeSessionField("versionNo", String(data.quoteVersionNo));
    }

    this.loaded = true;
  }

  loadFromCache(sessionId?: string | number): Observable<boolean> {
    const token = this.loginDetailsStorage.getItem("token");
    const sid = sessionId ?? this.getCacheSessionId();

    if (!token || !sid) {
      return of(false);
    }

    return this.sessionCacheApi.get<QuoteCacheDto>(CACHE_TYPE, sid).pipe(
      tap((data) => {
        if (data) {
          this.applyFromCacheDto(data);
        }
      }),
      map((data) => !!data),
      catchError(() => of(false))
    );
  }

  saveToCache(
    overrides: Partial<QuoteCacheDto> = {},
    sessionIdOverride?: string | number
  ): Observable<QuoteCacheDto | null> {
    const sessionId = sessionIdOverride ?? this.getCacheSessionId();
    if (!sessionId) {
      return of(null);
    }

    const payload = this.buildCachePayload(overrides);
    this.applyFromCacheDto(payload);

    return this.sessionCacheApi.set(CACHE_TYPE, sessionId, payload).pipe(
      map(() => payload),
      catchError(() => of(payload))
    );
  }

  removeFromCache(sessionId?: string | number): Observable<unknown> {
    const sid = sessionId ?? this.getCacheSessionId();
    if (!sid) {
      return of(null);
    }
    return this.sessionCacheApi.remove(CACHE_TYPE, sid);
  }

  private toCacheDateOrNull(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const trimmed = String(value).trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
      return null;
    }
    return trimmed;
  }

  toCacheInt(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === "") {
      return 0;
    }
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  }

  private getQuoteNo(): string {
    return this.getItem("quoteNo") ?? this.getItem("quoteNumber") ?? "";
  }

  private readSessionField(key: string): string | null {
    if (typeof window === "undefined") {
      return null;
    }
    return window.sessionStorage.getItem(key);
  }

  private writeSessionField(key: string, value: string): void {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(key, value);
  }

  private toCacheText(value: unknown): string {
    if (value === null || value === undefined) {
      return "";
    }
    const trimmed = String(value).trim();
    if (trimmed === "" || trimmed.toLowerCase() === "null" || trimmed.toLowerCase() === "undefined") {
      return "";
    }
    return trimmed;
  }

  private toCacheAddressId(value: string | null): number {
    if (!value) {
      return 0;
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return this.toCacheInt(parsed[0]);
      }
      return this.toCacheInt(parsed);
    } catch {
      return this.toCacheInt(value);
    }
  }

  private formatAddressIdForSession(key: string, addressId: number): string {
    if (!addressId) {
      return "";
    }
    const existing = this.getItem(key);
    if (existing?.startsWith("[")) {
      return existing;
    }
    return JSON.stringify([addressId]);
  }

  private resolveInceptionDescription(code: string): string {
    if (!code) {
      return "";
    }
    const listRaw = this.readSessionField("InceptionstatusList");
    if (!listRaw) {
      return "";
    }
    try {
      const list = JSON.parse(listRaw) as Record<
        string,
        { gencodeId?: number; code?: string; description?: string }
      >;
      const entry = list[code];
      return entry?.description ?? "";
    } catch {
      return "";
    }
  }

  private schedulePersist(): void {
    if (!this.persistEnabled) {
      return;
    }
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      const sessionId = this.getCacheSessionId();
      if (sessionId) {
        this.saveToCache().subscribe();
      }
    }, 300);
  }
}
